import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { addErrorNotification, configurationSettings, createInProgressItem, createInProgressItemWithPrice, getItemById, getOrderItemCount, isDiscountedPriceAvailableForSellableItem, isNormalItem, NotificationType } from '@vista/omnichannel-components-domain';
import { bemClassBuilder } from '@vista-digital/bem-class-builder';
import { observer } from 'mobx-react';
import { useCallback, useContext, useEffect, useState } from 'react';
import { useOccContext } from '../../../context/occ/hooks/use-occ-context';
import { getIdForComponentNotification } from '../../../utils/get-id-for-component-notification';
import { ItemDetails } from '../../item-details/item-details';
import { ItemImage } from '../../item-image/item-image';
import { PromotedTag, PromotedTagAlignment } from '../../promoted-tag/promoted-tag';
import { ItemPickerContextInstance } from '../types/context';
import { getDisplayPrice as getPrice } from '../utils/get-display-price';
import { getPromotionLabel } from '../utils/get-promotion-label';
import { ItemPickerItemModal } from './item-modal';
const b = bemClassBuilder('v-item-picker-item-button');
export const ItemPickerItemButton = observer(({ itemProfileButton }) => {
    const { i18n, displayText: occDisplayText } = useOccContext();
    const { itemLimitReachedMessage } = occDisplayText.order;
    const { displayText, events, config, cdnOptions } = useContext(ItemPickerContextInstance);
    const { readonly, itemButton } = config;
    const { disabled, showDescription, showPrice, showPromotionLabel } = itemButton;
    const [showModal, setShowModal] = useState(false);
    const item = getItemById(itemProfileButton.itemId);
    if (!item)
        throw new Error(`Cannot find item with ID: ${itemProfileButton.itemId}`);
    const promotionLabel = getPromotionLabel(itemProfileButton, displayText, occDisplayText, i18n);
    const hasPromotion = !!promotionLabel;
    const hasDiscount = isDiscountedPriceAvailableForSellableItem(itemProfileButton);
    const handleModalOpen = useCallback(() => {
        setShowModal(true);
    }, []);
    const handleModalClose = useCallback(() => {
        setShowModal(false);
    }, []);
    const handleClick = useCallback(() => {
        var _a;
        if (!readonly) {
            const allowedMaximum = !config.quantityLimit.isUnlimited
                ? config.quantityLimit.maximum
                : configurationSettings.data.order.maximumItemsPerOrder;
            if (getOrderItemCount() >= allowedMaximum) {
                addErrorNotification({
                    id: getIdForComponentNotification('item-picker', NotificationType.Error, 'order-item-limit-reached'),
                    messages: [itemLimitReachedMessage]
                });
                return;
            }
            if (hasPromotion && isNormalItem(item)) {
                createInProgressItemWithPrice(itemProfileButton);
            }
            else {
                createInProgressItem(itemProfileButton);
            }
        }
        handleModalOpen();
        (_a = events.onItemClicked) === null || _a === void 0 ? void 0 : _a.call(events, item);
    }, [
        config.quantityLimit,
        hasPromotion,
        item,
        itemProfileButton,
        handleModalOpen,
        events,
        readonly,
        itemLimitReachedMessage
    ]);
    useEffect(() => {
        return () => {
            handleModalClose();
        };
    }, [handleModalClose]);
    return (_jsxs("li", Object.assign({ className: b.block() }, { children: [_jsx("button", Object.assign({ disabled: disabled, className: b.element('button'), onClick: handleClick }, { children: _jsxs("div", Object.assign({ className: b.element('wrapper') }, { children: [_jsxs("div", Object.assign({ className: b.element('image-wrapper') }, { children: [_jsx(ItemImage, { item: item, className: b.element('image'), cdnOptions: cdnOptions.itemThumbnail }), _jsx("div", Object.assign({ className: b.element('hover-overlay') }, { children: _jsx("span", Object.assign({ className: b.element('hover-label') }, { children: displayText.itemButtonHoverLabel })) }))] })), _jsx(ItemDetails, { item: item, showPrice: showPrice, showDescription: showDescription, getDisplayPrice: getDisplayPrice(itemProfileButton, displayText, occDisplayText.price, i18n), priceFormat: { showDefaultPrice: hasDiscount } })] })) })), showPromotionLabel && promotionLabel && (_jsx(PromotedTag, { label: promotionLabel, className: b.element('promoted-tag'), alignment: PromotedTagAlignment.Left })), !disabled && (_jsx(ItemPickerItemModal, { item: item, itemProfileButton: itemProfileButton, show: showModal, onClose: handleModalClose, getDisplayPrice: getDisplayPrice(itemProfileButton, displayText, occDisplayText.price, i18n), promotionLabel: getPromotionLabel(itemProfileButton, displayText, occDisplayText, i18n) }))] })));
});
const getDisplayPrice = (sellableItem, displayText, priceDisplayText, i18n) => {
    return (priceFormat) => getPrice(sellableItem, displayText, priceDisplayText, priceFormat, i18n);
};
